import { useEffect, useState } from "react";
import {
    Button,
    Callout,
    Card,
    Heading,
    Modal,
    NumberField,
    PropertyList,
    PropertyListItem,
    SelectField,
    Spinner,
    Tab,
    TabGroup,
    Table,
    Tag,
    TextArea,
    TextField,
    formatCurrency,
    type TableColumn,
} from "@humanforce/mango-ui-react";
import { fetchOrganisations } from "../organisation/organisationHttpClient";
import { payClassificationsClient as client } from "./payClassificationsHttpClient";
import type {
    Agreement,
    Band,
    ClassificationDetail,
    ClassificationRow,
    CreateClassification,
    HistoryEntry,
    Rate,
} from "./payClassificationsApiTypes";

const today = () => new Date().toISOString().slice(0, 10);
const option = (value: string, label: string) => ({ value, label });
const text = (value: unknown) => (typeof value === "string" ? value : "");
const errorText = (error: unknown) =>
    error instanceof Error
        ? error.message
        : "The request could not be completed.";
const emptyClassification = (organisationId: string): CreateClassification => ({
    organisationId,
    code: "",
    name: "",
    type: "NON_AWARD",
    effectiveFrom: today(),
});

export const PayClassificationsScreen = () => {
    const [organisations, setOrganisations] = useState<
        Array<{ value: string; label: string }>
    >([]);
    const [organisationId, setOrganisationId] = useState("");
    const [rows, setRows] = useState<ClassificationRow[]>([]);
    const [selected, setSelected] = useState<ClassificationDetail | null>(null);
    const [agreements, setAgreements] = useState<Agreement[]>([]);
    const [history, setHistory] = useState<HistoryEntry[]>([]);
    const [awardLookup, setAwardLookup] = useState({
        available: false,
        message: "Checking WFM Award lookup…",
    });
    const [search, setSearch] = useState("");
    const [typeFilter, setTypeFilter] = useState("");
    const [statusFilter, setStatusFilter] = useState("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");
    const [dialog, setDialog] = useState<
        "classification" | "agreement" | "rate" | "band" | "edit" | "end" | "rateEnd" | "bandEnd" | null
    >(null);
    const [classificationForm, setClassificationForm] =
        useState<CreateClassification>(emptyClassification(""));
    const [agreementForm, setAgreementForm] = useState({
        code: "",
        name: "",
        agreementType: "ENTERPRISE_AGREEMENT",
        fwcApprovalReference: "",
        fwcApprovalDate: "",
        divisor: 52.14,
        nominalExpiryDate: "",
        effectiveFrom: today(),
        effectiveTo: "",
        source: "MANUAL",
    });
    const [rateForm, setRateForm] = useState({
        rateType: "ANNUAL",
        amount: 0,
        currencyCode: "AUD",
        effectiveFrom: today(),
        effectiveTo: "",
    });
    const [bandForm, setBandForm] = useState({
        rateType: "ANNUAL",
        minimum: 0,
        midpoint: null as number | null,
        maximum: 0,
        currencyCode: "AUD",
        effectiveFrom: today(),
        effectiveTo: "",
    });
    const [editForm, setEditForm] = useState({
        name: "",
        description: "",
        effectiveTo: today(),
    });
    const [saving, setSaving] = useState(false);
    const [detailTab, setDetailTab] = useState("overview");
    const [editingAgreementId, setEditingAgreementId] = useState<string | null>(null);
    const [effectiveItem, setEffectiveItem] = useState<Rate | Band | null>(null);

    useEffect(() => {
        void Promise.all([fetchOrganisations(), client.awardAvailability()])
            .then(([response, availability]) => {
                const options = (response.result ?? []).flatMap((org) =>
                    org.id ? [option(org.id, org.displayName || org.id)] : [],
                );
                setOrganisations(options);
                setOrganisationId(options[0]?.value ?? "");
                setAwardLookup(availability);
            })
            .catch((error) => setError(errorText(error)))
            .finally(() => setLoading(false));
    }, []);

    const load = async () => {
        if (!organisationId) return;
        setLoading(true);
        setError("");
        try {
            const [result, agreementRows] = await Promise.all([
                client.browse(
                    organisationId,
                    search.trim(),
                    typeFilter,
                    statusFilter,
                ),
                client.agreements(organisationId),
            ]);
            setRows(result.items);
            setAgreements(agreementRows);
            if (selected) {
                const refreshed = await client.get(organisationId, selected.id);
                setSelected(refreshed);
                setHistory(await client.history(organisationId, selected.id));
            }
        } catch (error) {
            setError(errorText(error));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => {
        const timer = setTimeout(() => void load(), 250);
        return () => clearTimeout(timer);
    }, [organisationId, search, typeFilter, statusFilter]);

    const openDetail = async (row: ClassificationRow) => {
        setError("");
        setDetailTab("overview");
        try {
            setSelected(await client.get(organisationId, row.id));
            setHistory(await client.history(organisationId, row.id));
        } catch (error) {
            setError(errorText(error));
        }
    };
    const openCreate = () => {
        setClassificationForm(emptyClassification(organisationId));
        setDialog("classification");
    };
    const openAgreementManager = () => {
        setEditingAgreementId(null);
        setAgreementForm({ code: "", name: "", agreementType: "ENTERPRISE_AGREEMENT", fwcApprovalReference: "", fwcApprovalDate: "", divisor: 52.14, nominalExpiryDate: "", effectiveFrom: today(), effectiveTo: "", source: "MANUAL" });
        setDialog("agreement");
    };
    const submit = async () => {
        setSaving(true);
        setError("");
        try {
            if (dialog === "classification") {
                await client.create({
                    ...classificationForm,
                    agreementId:
                        classificationForm.type === "AGREEMENT"
                            ? classificationForm.agreementId
                            : undefined,
                    wfmAwardId: undefined,
                });
                setNotice("Classification created.");
            }
            if (dialog === "agreement") {
                const payload = {
                    organisationId,
                    ...agreementForm,
                    fwcApprovalReference:
                        agreementForm.fwcApprovalReference || undefined,
                    fwcApprovalDate: agreementForm.fwcApprovalDate || undefined,
                    nominalExpiryDate:
                        agreementForm.nominalExpiryDate || undefined,
                    effectiveTo: agreementForm.effectiveTo || undefined,
                };
                if (editingAgreementId) {
                    const current = agreements.find((item) => item.id === editingAgreementId)!;
                    await client.updateAgreement(organisationId, editingAgreementId, { ...payload, expectedRowVersion: current.rowVersion });
                    setNotice("Agreement updated.");
                } else {
                    await client.createAgreement(payload);
                    setNotice("Agreement created.");
                }
            }
            if (dialog === "rate" && selected) {
                await client.createRate(organisationId, selected.id, {
                    ...rateForm,
                    effectiveTo: rateForm.effectiveTo || undefined,
                });
                setNotice("Future-dated rate added.");
            }
            if (dialog === "band" && selected) {
                await client.createBand(organisationId, selected.id, {
                    ...bandForm,
                    midpoint: bandForm.midpoint ?? undefined,
                    effectiveTo: bandForm.effectiveTo || undefined,
                });
                setNotice("Non-Award band added.");
            }
            if (dialog === "edit" && selected) {
                await client.update(organisationId, selected.id, {
                    name: editForm.name,
                    description: editForm.description || undefined,
                    expectedRowVersion: selected.rowVersion,
                });
                setNotice("Classification updated.");
            }
            if (dialog === "end" && selected) {
                await client.endDate(organisationId, selected.id, {
                    effectiveTo: editForm.effectiveTo,
                    expectedRowVersion: selected.rowVersion,
                });
                setNotice("Classification end date saved.");
            }
            if (dialog === "rateEnd" && selected && effectiveItem) {
                await client.endDateRate(organisationId, selected.id, effectiveItem.id, editForm.effectiveTo, effectiveItem.rowVersion);
                setNotice("Rate end date saved.");
            }
            if (dialog === "bandEnd" && selected && effectiveItem) {
                await client.endDateBand(organisationId, selected.id, effectiveItem.id, editForm.effectiveTo, effectiveItem.rowVersion);
                setNotice("Band end date saved.");
            }
            setDialog(null);
            await load();
        } catch (error) {
            setError(errorText(error));
        } finally {
            setSaving(false);
        }
    };

    const columns: TableColumn[] = [
        {
            field: "name",
            header: "Classification",
            sortable: true,
            body: (row: ClassificationRow) => (
                <span className="mgo-stack mgo-gap-2xs">
                    <span className="mgo-weight-strong">{row.name}</span>
                    <span className="mgo-text-xs mgo-color-muted">
                        {row.code}
                    </span>
                </span>
            ),
        },
        {
            field: "type",
            header: "Type",
            sortable: true,
            body: (row: ClassificationRow) => (
                <Tag label={row.type.replaceAll("_", " ")} tone="neutral" />
            ),
        },
        {
            field: "status",
            header: "Status",
            sortable: true,
            body: (row: ClassificationRow) => (
                <Tag
                    label={row.status}
                    tone={
                        row.status === "ACTIVE"
                            ? "positive"
                            : row.status === "FUTURE"
                              ? "highlight"
                              : "neutral"
                    }
                />
            ),
        },
        { field: "effectiveFrom", header: "Effective from", sortable: true },
    ];
    const typeOptions = [
        option("", "All types"),
        option("NON_AWARD", "Non-Award"),
        option("AGREEMENT", "Agreement"),
        option("AWARD_BACKED", "Award-backed"),
    ];
    const filteredTypeOptions = awardLookup.available
        ? typeOptions.slice(1)
        : typeOptions.slice(1, 3);
    const rateColumns: TableColumn[] = [
        { field: "rateType", header: "Rate type" },
        {
            field: "amount",
            header: "Amount",
            body: (row: Rate) => formatCurrency(row.amount, row.currencyCode),
        },
        { field: "effectiveFrom", header: "Effective from" },
        {
            field: "effectiveTo",
            header: "Effective to",
            body: (row: Rate) => row.effectiveTo || "Open",
        },
        { field: "actions", header: "", body: (row: Rate) => <Button label={`End-date ${row.rateType} rate`} size="small" variant="tertiary" onClick={() => { setEffectiveItem(row); setEditForm((current) => ({ ...current, effectiveTo: row.effectiveTo || today() })); setDialog("rateEnd"); }} /> },
    ];
    const bandColumns: TableColumn[] = [
        { field: "rateType", header: "Rate type" },
        {
            field: "minimum",
            header: "Minimum",
            body: (row: Band) => formatCurrency(row.minimum, row.currencyCode),
        },
        {
            field: "midpoint",
            header: "Midpoint",
            body: (row: Band) =>
                row.midpoint == null
                    ? "—"
                    : formatCurrency(row.midpoint, row.currencyCode),
        },
        {
            field: "maximum",
            header: "Maximum",
            body: (row: Band) => formatCurrency(row.maximum, row.currencyCode),
        },
        { field: "effectiveFrom", header: "Effective from" },
        { field: "actions", header: "", body: (row: Band) => <Button label={`End-date ${row.rateType} band`} size="small" variant="tertiary" onClick={() => { setEffectiveItem(row); setEditForm((current) => ({ ...current, effectiveTo: row.effectiveTo || today() })); setDialog("bandEnd"); }} /> },
    ];

    if (loading && organisations.length === 0)
        return (
            <div className="mgo-cluster mgo-gap-s" aria-live="polite">
                <Spinner />
                <span>Loading Pay Classifications…</span>
            </div>
        );
    return (
        <div
            className="mgo-stack mgo-gap-l"
            data-testid="pay-classifications-screen"
        >
            {notice && (
                <Callout tone="positive" title="Saved">
                    {notice}
                </Callout>
            )}
            {error && (
                <Callout tone="critical" title="Action could not be completed">
                    {error}
                </Callout>
            )}
            {!awardLookup.available && (
                <Callout
                    tone="caution"
                    title="Award-backed creation unavailable"
                >
                    {awardLookup.message} Existing validated Award-backed
                    classifications remain visible with their last-known
                    reference details.
                </Callout>
            )}
            <div className="mgo-split mgo-gap-l">
                <div className="mgo-cluster mgo-gap-m">
                    <SelectField
                        label="Organisation"
                        value={organisationId}
                        options={organisations}
                        onChange={(value) => {
                            setSelected(null);
                            setOrganisationId(text(value));
                        }}
                    />
                    <TextField
                        label="Search"
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                    />
                    <SelectField
                        label="Type"
                        searchable={false}
                        value={typeFilter}
                        options={typeOptions}
                        onChange={(value) => setTypeFilter(text(value))}
                    />
                    <SelectField
                        label="Status"
                        searchable={false}
                        value={statusFilter}
                        options={[
                            option("", "All statuses"),
                            option("ACTIVE", "Active"),
                            option("FUTURE", "Future"),
                            option("INACTIVE", "Inactive"),
                        ]}
                        onChange={(value) => setStatusFilter(text(value))}
                    />
                </div>
                <div className="mgo-cluster mgo-gap-s">
                    <Button
                        label="Manage Agreements"
                        variant="secondary"
                        onClick={openAgreementManager}
                    />
                    <Button
                        label="Add Classification"
                        onClick={openCreate}
                        disabled={!organisationId}
                    />
                </div>
            </div>
            <div className="mgo-grid mgo-grid-cols-1 mgo-grid-cols-desktop-2 mgo-gap-l">
                <Card title="Classification catalogue">
                    <Table<ClassificationRow>
                        value={rows}
                        columns={columns}
                        loading={loading}
                        emptyMessage="No Classifications match the selected filters."
                        onRowClick={(row) => void openDetail(row)}
                        paginated
                        rowsPerPage={20}
                        testId="pay-classifications-table"
                    />
                </Card>
                <Card
                    title={selected ? selected.name : "Classification details"}
                >
                    {!selected ? (
                        <Callout tone="neutral">
                            Choose a Classification to view its identity, rates,
                            bands and history.
                        </Callout>
                    ) : (
                        <div className="mgo-stack mgo-gap-l">
                            <div className="mgo-split">
                                <div>
                                    <Heading level={3}>{selected.name}</Heading>
                                    <span className="mgo-color-muted">
                                        {selected.code}
                                    </span>
                                </div>
                                <div className="mgo-cluster mgo-gap-s">
                                    <Button
                                        label="Edit"
                                        variant="secondary"
                                        onClick={() => {
                                            setEditForm({
                                                name: selected.name,
                                                description:
                                                    selected.description || "",
                                                effectiveTo:
                                                    selected.effectiveTo ||
                                                    today(),
                                            });
                                            setDialog("edit");
                                        }}
                                    />
                                    <Button
                                        label="End date"
                                        variant="secondary"
                                        onClick={() => {
                                            setEditForm((current) => ({
                                                ...current,
                                                effectiveTo:
                                                    selected.effectiveTo ||
                                                    today(),
                                            }));
                                            setDialog("end");
                                        }}
                                    />
                                </div>
                            </div>
              <TabGroup value={detailTab} onValueChange={setDetailTab}>
                                <Tab value="overview" label="Overview">
                                    <PropertyList orientation="vertical">
                                        <PropertyListItem label="Type">
                                            {selected.type.replaceAll("_", " ")}
                                        </PropertyListItem>
                                        <PropertyListItem label="Status">
                                            {selected.status}
                                        </PropertyListItem>
                                        <PropertyListItem label="Effective period">
                                            {selected.effectiveFrom} →{" "}
                                            {selected.effectiveTo || "Open"}
                                        </PropertyListItem>
                                        <PropertyListItem label="Agreement">
                                            {agreements.find(
                                                (item) =>
                                                    item.id ===
                                                    selected.agreementId,
                                            )?.name || "—"}
                                        </PropertyListItem>
                                        <PropertyListItem label="WFM Award">
                                            {selected.wfmAwardName ||
                                                selected.wfmAwardCode ||
                                                selected.wfmAwardId ||
                                                "—"}
                                            {selected.awardReferenceState
                                                ? ` (${selected.awardReferenceState})`
                                                : ""}
                                        </PropertyListItem>
                                    </PropertyList>
                                </Tab>
                                <Tab value="rates" label="Rates">
                                    <div className="mgo-stack mgo-gap-m">
                                        <Button
                                            label="Add future rate"
                                            size="small"
                                            onClick={() => setDialog("rate")}
                                        />
                                        <Table<Rate>
                                            value={selected.rates}
                                            columns={rateColumns}
                                            emptyMessage="No rates have been configured."
                                        />
                                    </div>
                                </Tab>
                                {selected.type === "NON_AWARD" && (
                                    <Tab value="bands" label="Non-Award bands">
                                        <div className="mgo-stack mgo-gap-m">
                                            <Button
                                                label="Add future band"
                                                size="small"
                                                onClick={() =>
                                                    setDialog("band")
                                                }
                                            />
                                            <Table<Band>
                                                value={selected.bands}
                                                columns={bandColumns}
                                                emptyMessage="No remuneration bands have been configured."
                                            />
                                        </div>
                                    </Tab>
                                )}
                                <Tab value="history" label="History">
                                    <div className="mgo-stack mgo-gap-s">
                                        {history.length === 0 ? (
                                            <Callout tone="neutral">
                                                No changes have been recorded.
                                            </Callout>
                                        ) : (
                                            history.map((entry) => (
                                                <div
                                                    key={entry.id}
                                                    className="mgo-split mgo-border-neutral-soft mgo-padding-s"
                                                >
                                                    <span>
                                                        {entry.operation.replaceAll(
                                                            "_",
                                                            " ",
                                                        )}
                                                    </span>
                                                    <span className="mgo-color-muted">
                                                        {new Date(
                                                            entry.occurredAt,
                                                        ).toLocaleString()}
                                                    </span>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </Tab>
                            </TabGroup>
                        </div>
                    )}
                </Card>
            </div>
            <Modal
                visible={dialog !== null}
                header={
                    dialog === "classification"
                        ? "Add Classification"
                        : dialog === "agreement"
                          ? editingAgreementId ? "Edit Agreement" : "Manage Agreements"
                          : dialog === "rate"
                            ? "Add future rate"
                            : dialog === "band"
                              ? "Add Non-Award band"
                              : dialog === "edit"
                                ? "Edit Classification"
                                : dialog === "rateEnd" ? "End-date rate" : dialog === "bandEnd" ? "End-date band" : "End-date Classification"
                }
                onHide={() => !saving && setDialog(null)}
                footer={
                    <>
                        <Button
                            label="Cancel"
                            variant="secondary"
                            disabled={saving}
                            onClick={() => setDialog(null)}
                        />
                        <Button
                            label="Save"
                            loading={saving}
                            onClick={() => void submit()}
                        />
                    </>
                }
            >
                <div className="mgo-stack mgo-gap-l">
                    {dialog === "classification" && (
                        <>
                            <TextField
                                label="Code"
                                required
                                value={classificationForm.code}
                                onChange={(event) =>
                                    setClassificationForm({
                                        ...classificationForm,
                                        code: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Name"
                                required
                                value={classificationForm.name}
                                onChange={(event) =>
                                    setClassificationForm({
                                        ...classificationForm,
                                        name: event.target.value,
                                    })
                                }
                            />
                            <SelectField
                                label="Type"
                                required
                                searchable={false}
                                value={classificationForm.type}
                                options={filteredTypeOptions}
                                onChange={(value) =>
                                    setClassificationForm({
                                        ...classificationForm,
                                        type: text(value),
                                        agreementId: undefined,
                                    })
                                }
                            />
                            {classificationForm.type === "AGREEMENT" && (
                                <SelectField
                                    label="Agreement"
                                    required
                                    value={classificationForm.agreementId ?? ""}
                                    options={agreements.map((item) =>
                                        option(
                                            item.id,
                                            `${item.code} — ${item.name}`,
                                        ),
                                    )}
                                    onChange={(value) =>
                                        setClassificationForm({
                                            ...classificationForm,
                                            agreementId: text(value),
                                        })
                                    }
                                />
                            )}
                            {classificationForm.type === "AWARD_BACKED" && (
                                <Callout tone="caution">
                                    Use of Award-backed Classifications requires
                                    the real WFM Award selector. Manual Award
                                    IDs are not accepted.
                                </Callout>
                            )}
                            <TextField
                                label="Effective from"
                                required
                                value={classificationForm.effectiveFrom}
                                onChange={(event) =>
                                    setClassificationForm({
                                        ...classificationForm,
                                        effectiveFrom: event.target.value,
                                    })
                                }
                            />
                            <TextArea
                                label="Description"
                                value={classificationForm.description ?? ""}
                                onChange={(event) =>
                                    setClassificationForm({
                                        ...classificationForm,
                                        description: event.target.value,
                                    })
                                }
                            />
                        </>
                    )}
                    {dialog === "agreement" && (
                        <>
                            <Callout
                                tone="neutral"
                                title="Agreement maintenance"
                            >
                                Agreements are Organisation-scoped and limited
                                to Classification maintenance.
                            </Callout>
                            {editingAgreementId && <Button label="Add Agreement" variant="secondary" onClick={openAgreementManager} />}
                            {agreements.length > 0 && <div className="mgo-stack mgo-gap-xs"><span className="mgo-weight-strong">Existing Agreements</span>{agreements.map((agreement) => <Button key={agreement.id} label={`${agreement.code} — ${agreement.name} (${agreement.status})`} variant="tertiary" onClick={() => { setEditingAgreementId(agreement.id); setAgreementForm({ code: agreement.code, name: agreement.name, agreementType: agreement.agreementType, fwcApprovalReference: agreement.fwcApprovalReference || "", fwcApprovalDate: agreement.fwcApprovalDate || "", divisor: agreement.divisor, nominalExpiryDate: agreement.nominalExpiryDate || "", effectiveFrom: agreement.effectiveFrom, effectiveTo: agreement.effectiveTo || "", source: agreement.source }); }} />)}</div>}
                            <TextField
                                label="Code"
                                required
                                value={agreementForm.code}
                                onChange={(event) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        code: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Name"
                                required
                                value={agreementForm.name}
                                onChange={(event) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        name: event.target.value,
                                    })
                                }
                            />
                            <SelectField
                                label="Agreement type"
                                value={agreementForm.agreementType}
                                options={[
                                    option(
                                        "ENTERPRISE_AGREEMENT",
                                        "Enterprise agreement",
                                    ),
                                    option(
                                        "REGISTERED_AGREEMENT",
                                        "Registered agreement",
                                    ),
                                    option(
                                        "GREENFIELDS_AGREEMENT",
                                        "Greenfields agreement",
                                    ),
                                ]}
                                onChange={(value) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        agreementType: text(value),
                                    })
                                }
                            />
                            <TextField
                                label="FWC approval reference"
                                value={agreementForm.fwcApprovalReference}
                                onChange={(event) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        fwcApprovalReference:
                                            event.target.value,
                                    })
                                }
                            />
                            <TextField label="FWC approval date" value={agreementForm.fwcApprovalDate} onChange={(event) => setAgreementForm({ ...agreementForm, fwcApprovalDate: event.target.value })} />
                            <NumberField
                                label="Weeks-per-year divisor"
                                min={0.0001}
                                value={agreementForm.divisor}
                                onChange={(value) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        divisor: value ?? 52.14,
                                    })
                                }
                            />
                            <TextField
                                label="Effective from"
                                disabled={editingAgreementId !== null}
                                value={agreementForm.effectiveFrom}
                                onChange={(event) =>
                                    setAgreementForm({
                                        ...agreementForm,
                                        effectiveFrom: event.target.value,
                                    })
                                }
                            />
                            <TextField label="Nominal expiry" value={agreementForm.nominalExpiryDate} onChange={(event) => setAgreementForm({ ...agreementForm, nominalExpiryDate: event.target.value })} />
                            <TextField label="Effective to" value={agreementForm.effectiveTo} onChange={(event) => setAgreementForm({ ...agreementForm, effectiveTo: event.target.value })} />
                            <SelectField label="Source" value={agreementForm.source} options={[option("MANUAL", "Manual"), option("IMPORT", "Import")]} onChange={(value) => setAgreementForm({ ...agreementForm, source: text(value) })} />
                        </>
                    )}
                    {dialog === "rate" && (
                        <>
                            <TextField
                                label="Rate type"
                                required
                                value={rateForm.rateType}
                                onChange={(event) =>
                                    setRateForm({
                                        ...rateForm,
                                        rateType: event.target.value,
                                    })
                                }
                            />
                            <NumberField
                                label="Amount"
                                min={0}
                                value={rateForm.amount}
                                onChange={(value) =>
                                    setRateForm({
                                        ...rateForm,
                                        amount: value ?? 0,
                                    })
                                }
                            />
                            <TextField
                                label="Currency code"
                                value={rateForm.currencyCode}
                                onChange={(event) =>
                                    setRateForm({
                                        ...rateForm,
                                        currencyCode: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Effective from"
                                value={rateForm.effectiveFrom}
                                onChange={(event) =>
                                    setRateForm({
                                        ...rateForm,
                                        effectiveFrom: event.target.value,
                                    })
                                }
                            />
                        </>
                    )}
                    {dialog === "band" && (
                        <>
                            <TextField
                                label="Rate type"
                                required
                                value={bandForm.rateType}
                                onChange={(event) =>
                                    setBandForm({
                                        ...bandForm,
                                        rateType: event.target.value,
                                    })
                                }
                            />
                            <div className="mgo-grid mgo-grid-cols-3 mgo-gap-m">
                                <NumberField
                                    label="Minimum"
                                    min={0}
                                    value={bandForm.minimum}
                                    onChange={(value) =>
                                        setBandForm({
                                            ...bandForm,
                                            minimum: value ?? 0,
                                        })
                                    }
                                />
                                <NumberField
                                    label="Midpoint"
                                    min={0}
                                    value={bandForm.midpoint}
                                    onChange={(value) =>
                                        setBandForm({
                                            ...bandForm,
                                            midpoint: value,
                                        })
                                    }
                                />
                                <NumberField
                                    label="Maximum"
                                    min={0}
                                    value={bandForm.maximum}
                                    onChange={(value) =>
                                        setBandForm({
                                            ...bandForm,
                                            maximum: value ?? 0,
                                        })
                                    }
                                />
                            </div>
                            <TextField
                                label="Currency code"
                                value={bandForm.currencyCode}
                                onChange={(event) =>
                                    setBandForm({
                                        ...bandForm,
                                        currencyCode: event.target.value,
                                    })
                                }
                            />
                            <TextField
                                label="Effective from"
                                value={bandForm.effectiveFrom}
                                onChange={(event) =>
                                    setBandForm({
                                        ...bandForm,
                                        effectiveFrom: event.target.value,
                                    })
                                }
                            />
                        </>
                    )}
                    {dialog === "edit" && (
                        <>
                            <TextField
                                label="Name"
                                value={editForm.name}
                                onChange={(event) =>
                                    setEditForm({
                                        ...editForm,
                                        name: event.target.value,
                                    })
                                }
                            />
                            <TextArea
                                label="Description"
                                value={editForm.description}
                                onChange={(event) =>
                                    setEditForm({
                                        ...editForm,
                                        description: event.target.value,
                                    })
                                }
                            />
                            <Callout tone="neutral">
                                Code, type, Organisation and instrument
                                association are stable and cannot be edited.
                            </Callout>
                        </>
                    )}
                    {dialog === "end" && (
                        <>
                            <TextField
                                label="Effective to"
                                value={editForm.effectiveTo}
                                onChange={(event) =>
                                    setEditForm({
                                        ...editForm,
                                        effectiveTo: event.target.value,
                                    })
                                }
                            />
                            <Callout tone="caution">
                                End-dating deactivates the Classification
                                without deleting rates, bands or history.
                            </Callout>
                        </>
                    )}
                    {(dialog === "rateEnd" || dialog === "bandEnd") && <><TextField label="Effective to" value={editForm.effectiveTo} onChange={(event) => setEditForm({ ...editForm, effectiveTo: event.target.value })} /><Callout tone="caution">Historical rows are immutable. The end date must be today or later and will be saved with optimistic concurrency.</Callout></>}
                </div>
            </Modal>
        </div>
    );
};
